import React from "react";
import { NavItem } from "components/ui/FormProgressSidebar";
import { OnboardingTask, OnboardingTaskName } from "types/onboarding";
import { FormStep, FormStepStatus } from "types/task";
import { ApiUserProfile } from "utils/api/user";
import SidebarInfo from "components/ui/SidebarInfo";
import { ProductName } from "types/general";
import { InfoType } from "../types";
import {
  DEFAULT_PRODUCT_CONFIG,
  DEFAULT_TASK_CONTENT_MAP,
  PRODUCT_CONFIG,
  PRODUCT_NAME_TO_FLOW_MAP,
  TASK_CONTENT_MAP,
} from "./constants";

const getLoginPath = (productName: ProductName) => {
  let path = "/users/sign_in";

  if (productName) {
    path += `?flow=${PRODUCT_NAME_TO_FLOW_MAP[productName]}`;
  }

  return path;
};

export const getProductConfig = (productName: ProductName) =>
  PRODUCT_CONFIG[productName] || DEFAULT_PRODUCT_CONFIG;

export const getSidebarNavigation = ({
  userProfile,
  stopImpersonatingUser,
  logout,
  withAuth,
  productName,
}: {
  userProfile?: ApiUserProfile;
  stopImpersonatingUser?: () => void;
  logout?: () => void;
  withAuth?: boolean;
  productName?: ProductName;
} = {}) => {
  const navigation: NavItem[] = [];
  if (userProfile?.impersonating) {
    navigation.push({
      text: "Stop Viewing as User",
      icon: "userCircle",
      onClick: stopImpersonatingUser,
    });
  }

  if (withAuth) {
    if (userProfile?.eligibleForCard) {
      navigation.push({
        text: "Save & Exit",
        icon: "arrowLeft",
        href: "/dashboard",
      });
    } else {
      navigation.push({
        text: "Log Out",
        icon: "doorOpen",
        onClick: logout,
      });
    }
  } else {
    navigation.push({
      text: "Log In",
      icon: "userCircle",
      href: getLoginPath(productName),
    });
  }

  return navigation;
};

export const getMobileNavigation = ({
  userProfile,
  logout,
  withAuth,
  productName,
}: {
  userProfile?: ApiUserProfile;
  logout?: () => void;
  withAuth?: boolean;
  productName?: ProductName;
} = {}) => {
  if (withAuth) {
    if (userProfile?.eligibleForCard) {
      return [
        {
          text: "Save & Exit",
          href: "/dashboard",
        },
      ];
    }
    return [
      {
        text: "Log Out",
        onClick: logout,
      },
    ];
  }

  return [
    {
      text: "Log In",
      href: getLoginPath(productName),
    },
  ];
};

const TASKS_WITHOUT_UI_STEP = [
  OnboardingTaskName.termsAcceptance,
  OnboardingTaskName.identityVerification,
  OnboardingTaskName.repaymentSetup,
];

export const mapTasksToSteps = ({
  tasks,
  productName,
}: {
  tasks: OnboardingTask[];
  productName: ProductName;
}) => {
  const taskMap = tasks.reduce(
    (acc, task) => ({ ...acc, [task.name]: task.state }),
    {}
  );

  return tasks.reduce((acc, task, taskIndex) => {
    if (TASKS_WITHOUT_UI_STEP.includes(task.name)) return acc; // Some tasks are not displayed separately in the sidebar

    let status = task.state;

    if (task.name === OnboardingTaskName.personalInfoSubmission) {
      if (
        [
          taskMap[OnboardingTaskName.termsAcceptance],
          taskMap[OnboardingTaskName.personalInfoSubmission],
        ].includes(FormStepStatus.ready)
      ) {
        // Terms acceptance and personal info submission are displayed as one step in the sidebar
        status = FormStepStatus.ready;
      }

      if (
        tasks[taskIndex + 1]?.name ===
          OnboardingTaskName.identityVerification &&
        taskMap[OnboardingTaskName.identityVerification] ===
          FormStepStatus.ready
      ) {
        // If identity verification immediately follows personal info submission, display them as one step
        status = FormStepStatus.ready;
      }
    }

    const contentMap =
      TASK_CONTENT_MAP[productName] || DEFAULT_TASK_CONTENT_MAP;
    const content = contentMap[task.name];

    const step = {
      id: task.name,
      status,
      ...content,
    };

    return [...acc, step];
  }, []) as FormStep[];
};

const INFO: {
  [key in InfoType]: { heading?: React.ReactNode; children: React.ReactNode };
} = {
  phone: {
    heading: "I don't have a US phone number",
    children:
      "Reset currently requires a US phone number for verification purposes.",
  },
  address: {
    heading: "I don't have a US home address",
    children:
      "Reset currently requires a US home address to verify your identity and mail your Reset card.",
  },
  patriotAct: {
    heading: "Patriot Act Disclosure",
    children: (
      <>
        <p>
          To help the government fight the funding of terrorism and money
          laundering activities, federal law requires all financial institutions
          to obtain, verify, and record information that identifies each person
          who opens an account.
        </p>
        <p>
          What this means for you: When you open an account, we will ask for
          your name, address, date of birth, and other information that will
          allow us to identify you. We may also ask to see your driver&apos;s
          license or other identifying documents.
        </p>
      </>
    ),
  },
};

export const getInfo = (infoType: string) =>
  INFO[infoType] || { heading: "", children: "" };

export const getShowInfo =
  ({ openWith, close }) =>
  (infoType: InfoType) =>
  () => {
    openWith(<SidebarInfo closeDrawer={close} {...getInfo(infoType)} />);
  };
